<template>
  <q-card class="booking-card" flat bordered>
    <q-expansion-item
      dense
      expand-separator
      :icon="userIcon"
      :label="userLabel"
      header-class="text-weight-bold bg-secondary text-white"
    >
      <q-card-section class="q-py-sm q-pb-none text-caption text-grey-8">
        <div v-if="userEmail" class="q-mb-xs">
          <q-icon name="mail" class="q-mr-xs" size="16px" />
          <a :href="'mailto:' + userEmail" class="text-grey-8">
            {{ userEmail }}
          </a>
        </div>
        <div v-if="userRegion" class="q-mb-xs">
          <q-icon name="location_on" class="q-mr-xs" size="16px" />
          {{ userRegion }}
        </div>
        <div v-if="userUrl" class="q-mb-xs">
          <q-icon name="public" class="q-mr-xs" size="16px" />
          <a :href="userUrl" target="_blank" class="text-primary">
            {{ $t('bookings.viewProfile') }}
          </a>
        </div>
        <div v-if="!hasUserDetails">
          {{ noDetailsText }}
        </div>
      </q-card-section>
    </q-expansion-item>

    <q-card-section class="q-pt-sm">
      <div class="text-caption text-grey-7">
        <q-icon name="event" class="q-mr-xs" />
        {{ $t('bookings.bookingDate') }}: {{ formatDate(booking.date) }}
      </div>

      <div class="text-caption text-grey-7">
        <q-icon name="send" class="q-mr-xs" />
        {{ $t('bookings.createdAt') }}: {{ formatDate(booking.created_at) }}
      </div>

      <div class="text-caption text-grey-7">
        <q-icon name="money" class="q-mr-xs" />
        {{ $t('bookings.rateLabel') }}:
        {{ booking.rate + '' + getCurrencySymbol(booking.currency) }}
        <q-icon
          v-if="userType === 'photographer'"
          name="info"
          size="14px"
          class="q-ml-xs text-grey-5 cursor-pointer"
        >
          <q-tooltip max-width="200px" class="text-caption">
            {{ $t('bookings.rateChangeHint') }}
          </q-tooltip>
        </q-icon>
      </div>

      <div class="text-caption text-grey-7 q-mb-md">
        <q-icon name="schedule" class="q-mr-xs" />
        {{ $t('bookings.statusLabel') }}:
        <q-badge
          :color="getStatusColor(booking.status)"
          class="text-weight-bold q-pa-xs"
        >
          {{ getStatusLabel(booking.status) }}
        </q-badge>
      </div>

      <div v-if="$slots.actions" class="q-mt-sm">
        <slot name="actions" :booking="booking" />
      </div>
    </q-card-section>

    <q-expansion-item
      dense
      expand-separator
      icon="chat"
      :label="$t('bookings.detailsMessage')"
      header-class="text-black bg-grey-4"
    >
      <q-card-section class="text-caption text-grey-8">
        {{ booking.message || $t('bookings.noAdditionalMessage') }}
      </q-card-section>
    </q-expansion-item>
  </q-card>
</template>

<script>
import BookingsMixin from '@/pages/bookings/mixins/BookingsMixin';
import userRoleMixin from '@/mixins/userRoleMixin';

export default {
  name: 'BookingsCard',
  mixins: [BookingsMixin, userRoleMixin],
  props: {
    booking: {
      type: Object,
      required: true,
    },
    userType: {
      type: String,
      required: true,
      validator: (val) => ['organizer', 'photographer'].includes(val),
    },
  },
  computed: {
    user() {
      return this.userType === 'photographer'
        ? this.booking.photographer
        : this.booking.organizer;
    },
    userIcon() {
      return this.userType === 'photographer' ? 'account_circle' : 'person';
    },
    userLabel() {
      const prefix =
        this.userType === 'photographer'
          ? this.$t('bookings.bookingWith')
          : this.$t('bookings.fromOrganizer');
      return `${prefix} ${this.displayNameOrUsernameFallback(this.user)}`;
    },
    userEmail() {
      return this.user?.email;
    },
    userRegion() {
      return this.user?.photographer_region;
    },
    userUrl() {
      return this.user?.photographer_landing_url;
    },
    hasUserDetails() {
      return !!(this.userEmail || this.userRegion || this.userUrl);
    },
    noDetailsText() {
      return this.userType === 'photographer'
        ? this.$t('bookings.noPhotographerDetails')
        : this.$t('bookings.noOrganizerDetails');
    },
  },
};
</script>
